export interface Airport {
  iata: string;
  icao: string;
  name: string;
  city: string;
  region: string;
  country: string;
  lat: number;
  lon: number;
  tz: string;
}

export interface AirportsFile {
  fields: string[];
  source: string;
  rows: [string, string, string, string, string, string, number, number, string][];
}

export function airportFromRow(r: AirportsFile['rows'][number]): Airport {
  return {
    iata: r[0],
    icao: r[1],
    name: r[2],
    city: r[3],
    region: r[4],
    country: r[5],
    lat: r[6],
    lon: r[7],
    tz: r[8],
  };
}

export function indexAirports(file: AirportsFile): Map<string, Airport> {
  const m = new Map<string, Airport>();
  for (const r of file.rows) m.set(r[0], airportFromRow(r));
  return m;
}

/** City name the way people say it: 'Minneapolis' rather than the airport's legal city. */
export function airportCity(a: Airport): string {
  return a.city || a.name.replace(/ (International )?Airport$/, '');
}

/** Short display name: 'Minneapolis-St Paul International'. */
export function airportShortName(a: Airport): string {
  return a.name.replace(/\/.*$/, '').replace(/ Airport$/, '');
}

const US_STATES: Record<string, string> = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  'District of Columbia': 'DC',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
  'Puerto Rico': 'PR',
  Guam: 'GU',
  'U.S. Virgin Islands': 'VI',
};

/** Two-letter code for a US state or territory name, if it is one. */
export function usStateCode(region: string | null | undefined): string | null {
  if (!region) return null;
  return US_STATES[region] ?? (Object.values(US_STATES).includes(region) ? region : null);
}

export function countryName(code: string, locale = 'en'): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}
